import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
    FastForward,
    Pause,
    Play,
    Repeat,
    Rewind,
    RotateCcw,
    SkipBack,
    SkipForward,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { actionPillClass, hairlineClass } from "@/lib/card-styles";

/**
 * Transport controls for a caption clip.
 *
 * The YouTube embed's own controls are built for watching a video; this is built
 * for *using* one line of it. A learner working a single phrase needs "back five
 * seconds" and "say that again" as one tap, not a scrub bar they have to aim at,
 * and "next line" without going back to the list.
 *
 * Everything here goes through `MultiPlatformPlayer`'s ref, so it is a no-op on a
 * platform that cannot seek (TikTok, Instagram, Facebook, X). The caller passes
 * `canControl` so those get a disabled bar rather than buttons that do nothing.
 */

/** One tap is a phrase, not a syllable. Five seconds is roughly a spoken line. */
export const SEEK_STEP_SECONDS = 5;

/** Shadowing ladder. Slow enough to catch the sounds, fast enough to stay musical. */
export const PLAYBACK_RATES = [1, 0.75, 0.5] as const;

export interface ClipTransportProps {
    /** False on platforms whose embeds cannot seek; every control renders disabled. */
    canControl: boolean;
    isPlaying: boolean;
    onTogglePlay: () => void;
    onSeekBy: (deltaSeconds: number) => void;
    /** Restart the current clip from its first word. */
    onReplay: () => void;
    onPrevious: () => void;
    onNext: () => void;
    hasPrevious: boolean;
    hasNext: boolean;
    /** Loops the clip instead of stopping at its end. */
    loop: boolean;
    onToggleLoop: () => void;
    rate: number;
    onCycleRate: () => void;
    /** Rendered above the buttons inside the same control block (the live caption). */
    children?: ReactNode;
    className?: string;
}

export function ClipTransport({
    canControl,
    isPlaying,
    onTogglePlay,
    onSeekBy,
    onReplay,
    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
    loop,
    onToggleLoop,
    rate,
    onCycleRate,
    children,
    className,
}: ClipTransportProps) {
    const { t } = useTranslation();
    const disabled = !canControl;

    const pill = (active = false) =>
        cn(
            actionPillClass,
            "inline-flex items-center justify-center text-muted-foreground transition-colors",
            "hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40",
            active && "border-primary/40 bg-primary/10 text-primary",
        );

    return (
        <div
            className={cn(
                "px-4 py-3",
                hairlineClass,
                "border-t",
                className,
            )}
        >
            {/* Buttons first: the live caption below them is the full spoken
                line now, so a long sentence grows the block downward. With the
                caption above, a 500-character line pushed play/pause and the
                speed ladder out of a dialog's view. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1" role="group" aria-label={t("pages.videos.inContext.transport.clipGroup", "Clip")}>
                <button
                    type="button"
                    className={pill()}
                    onClick={onPrevious}
                    disabled={disabled || !hasPrevious}
                    aria-label={t("pages.videos.inContext.transport.previous", "Previous clip")}
                    title={t("pages.videos.inContext.transport.previous", "Previous clip")}
                >
                    <SkipBack className="h-4 w-4" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className={pill()}
                    onClick={() => onSeekBy(-SEEK_STEP_SECONDS)}
                    disabled={disabled}
                    aria-label={t("pages.videos.inContext.transport.back", "Back 5 seconds")}
                    title={t("pages.videos.inContext.transport.back", "Back 5 seconds")}
                >
                    <Rewind className="h-4 w-4" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className={cn(pill(), "h-10 w-10")}
                    onClick={onTogglePlay}
                    disabled={disabled}
                    aria-label={
                        isPlaying
                            ? t("pages.videos.inContext.transport.pause", "Pause")
                            : t("pages.videos.inContext.transport.play", "Play")
                    }
                    title={
                        isPlaying
                            ? t("pages.videos.inContext.transport.pause", "Pause")
                            : t("pages.videos.inContext.transport.play", "Play")
                    }
                >
                    {isPlaying ? (
                        <Pause className="h-4 w-4 fill-current" aria-hidden="true" />
                    ) : (
                        <Play className="h-4 w-4 fill-current" aria-hidden="true" />
                    )}
                </button>

                <button
                    type="button"
                    className={pill()}
                    onClick={() => onSeekBy(SEEK_STEP_SECONDS)}
                    disabled={disabled}
                    aria-label={t("pages.videos.inContext.transport.forward", "Forward 5 seconds")}
                    title={t("pages.videos.inContext.transport.forward", "Forward 5 seconds")}
                >
                    <FastForward className="h-4 w-4" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className={pill()}
                    onClick={onNext}
                    disabled={disabled || !hasNext}
                    aria-label={t("pages.videos.inContext.transport.next", "Next clip")}
                    title={t("pages.videos.inContext.transport.next", "Next clip")}
                >
                    <SkipForward className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>

            <div className="flex items-center gap-1" role="group" aria-label={t("pages.videos.inContext.transport.playbackGroup", "Playback")}>
                <button
                    type="button"
                    className={pill()}
                    onClick={onReplay}
                    disabled={disabled}
                    aria-label={t("pages.videos.inContext.transport.replay", "Say it again")}
                    title={t("pages.videos.inContext.transport.replay", "Say it again")}
                >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className={pill(loop)}
                    onClick={onToggleLoop}
                    disabled={disabled}
                    aria-pressed={loop}
                    aria-label={t("pages.videos.inContext.transport.loop", "Repeat this clip")}
                    title={t("pages.videos.inContext.transport.loop", "Repeat this clip")}
                >
                    <Repeat className="h-4 w-4" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className={cn(pill(rate !== 1), "w-auto px-2.5 tabular-nums")}
                    onClick={onCycleRate}
                    disabled={disabled}
                    aria-label={t("pages.videos.inContext.transport.speed", "Playback speed")}
                    title={t("pages.videos.inContext.transport.speed", "Playback speed")}
                >
                    <span className="text-[11px] font-semibold">{rate}×</span>
                </button>
            </div>
            </div>

            {children ? <div className="mt-2">{children}</div> : null}
        </div>
    );
}
